<template>
    <transition name="iu-drawer-fade" @after-enter="afterEnter" @after-leave="afterLeave">
        <div class="iu-drawer__wrapper" tabindex="-1" v-show="visible">
            <div
                class="iu-drawer__container"
                :class="visible && 'iu-drawer__open'"
                @click.self="handleWrapperClick"
                role="document"
                tabindex="-1"
            >
                <div
                    aria-modal="true"
                    aria-labelledby="iu-drawer__title"
                    :aria-label="title"
                    class="iu-drawer"
                    :class="[direction, customClass]"
                    :style="isHorizontal ? `width: ${size}` : `height: ${size}`"
                    ref="drawer"
                    role="dialog"
                    tabindex="-1"
                >
                    <header class="iu-drawer__header" id="iu-drawer__title" v-if="withHeader">
                        <slot name="title">
                            <span role="heading" tabindex="0" :title="title">{{ title }}</span>
                        </slot>
                        <button
                            :aria-label="`close ${title || 'drawer'}`"
                            class="iu-drawer__close-btn"
                            type="button"
                            v-if="showClose"
                            @click="closeDrawer"
                        >
                            <i class="iu-dialog__close iu-icon iu-icon-close"></i>
                        </button>
                    </header>
                    <section class="iu-drawer__body" v-if="rendered">
                        <slot></slot>
                    </section>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import Popup from '../../../utils/popup';
import emitter from '../../../utils/emitter';
import Utils from '../../../utils/aria-utils';
export default {
    name: 'IuDrawer',
    mixins: [Popup, emitter],
    props: {
        appendToBody: {
            type: Boolean,
            default: false,
        },
        beforeClose: {
            type: Function,
        },
        customClass: {
            type: String,
            default: '',
        },
        closeOnPressEscape: {
            type: Boolean,
            default: true,
        },
        destroyOnClose: {
            type: Boolean,
            default: false,
        },
        modal: {
            type: Boolean,
            default: true,
        },
        direction: {
            type: String,
            default: 'rtl',
            validator(val) {
                return ['ltr', 'rtl', 'ttb', 'btt'].indexOf(val) !== -1;
            },
        },
        modalAppendToBody: {
            type: Boolean,
            default: true,
        },
        showClose: {
            type: Boolean,
            default: true,
        },
        size: {
            type: String,
            default: '30%',
        },
        title: {
            type: String,
            default: '',
        },
        visible: {
            type: Boolean,
        },
        wrapperClosable: {
            type: Boolean,
            default: true,
        },
        withHeader: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        isHorizontal() {
            return this.direction === 'rtl' || this.direction === 'ltr';
        },
    },
    data() {
        return {
            closed: false,
            prevActiveElement: null,
        };
    },
    watch: {
        visible(val) {
            if (val) {
                this.closed = false;
                this.$emit('open');
                if (this.appendToBody) {
                    document.body.appendChild(this.$el);
                }
                this.prevActiveElement = document.activeElement;
                this.$nextTick(() => {
                    Utils.focusFirstDescendant(this.$refs.drawer);
                });
            } else {
                if (!this.closed) this.$emit('close');
                this.$nextTick(() => {
                    if (this.prevActiveElement) {
                        this.prevActiveElement.focus();
                    }
                });
            }
        },
    },
    methods: {
        afterEnter() {
            this.$emit('opened');
        },
        afterLeave() {
            this.$emit('closed');
        },
        hide(cancel) {
            if (cancel !== false) {
                this.$emit('update:visible', false);
                this.$emit('close');
                if (this.destroyOnClose === true) {
                    this.rendered = false;
                }
                this.closed = true;
            }
        },
        handleWrapperClick() {
            if (this.wrapperClosable) {
                this.closeDrawer();
            }
        },
        closeDrawer() {
            if (typeof this.beforeClose === 'function') {
                this.beforeClose(this.hide);
            } else {
                this.hide();
            }
        },
        handleClose() {
            // This method here will be called by PopupManger, when the `closeOnPressEscape` was set to true
            // pressing `ESC` will call this method, and also close the drawer.
            // This method also calls `beforeClose` if there was one.
            this.closeDrawer();
        },
    },
    mounted() {
        if (this.visible) {
            this.rendered = true;
            this.open();
        }
    },
    destroyed() {
        // if appendToBody is true, remove DOM node after destroy
        if (this.appendToBody && this.$el && this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el);
        }
    },
};
</script>

<style scope>
.iu-drawer.ltr,
.iu-drawer.rtl,
.iu-drawer__container {
    top: 0;
    bottom: 0;
    height: 100%;
}
.iu-drawer.btt,
.iu-drawer.ttb,
.iu-drawer__container {
    left: 0;
    right: 0;
    width: 100%;
}
@-webkit-keyframes iu-drawer-fade-in {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}
@keyframes iu-drawer-fade-in {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}
@-webkit-keyframes rtl-drawer-in {
    0% {
        -webkit-transform: translate(100%, 0);
        transform: translate(100%, 0);
    }
    100% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
}
@keyframes rtl-drawer-in {
    0% {
        -webkit-transform: translate(100%, 0);
        transform: translate(100%, 0);
    }
    100% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
}
@-webkit-keyframes rtl-drawer-out {
    0% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
    100% {
        -webkit-transform: translate(100%, 0);
        transform: translate(100%, 0);
    }
}
@keyframes rtl-drawer-out {
    0% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
    100% {
        -webkit-transform: translate(100%, 0);
        transform: translate(100%, 0);
    }
}
@-webkit-keyframes ltr-drawer-in {
    0% {
        -webkit-transform: translate(-100%, 0);
        transform: translate(-100%, 0);
    }
    100% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
}
@keyframes ltr-drawer-in {
    0% {
        -webkit-transform: translate(-100%, 0);
        transform: translate(-100%, 0);
    }
    100% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
}
@-webkit-keyframes ltr-drawer-out {
    0% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
    100% {
        -webkit-transform: translate(-100%, 0);
        transform: translate(-100%, 0);
    }
}
@keyframes ltr-drawer-out {
    0% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
    100% {
        -webkit-transform: translate(-100%, 0);
        transform: translate(-100%, 0);
    }
}
@-webkit-keyframes ttb-drawer-in {
    0% {
        -webkit-transform: translate(0, -100%);
        transform: translate(0, -100%);
    }
    100% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
}
@keyframes ttb-drawer-in {
    0% {
        -webkit-transform: translate(0, -100%);
        transform: translate(0, -100%);
    }
    100% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
}
@-webkit-keyframes ttb-drawer-out {
    0% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
    100% {
        -webkit-transform: translate(0, -100%);
        transform: translate(0, -100%);
    }
}
@keyframes ttb-drawer-out {
    0% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
    100% {
        -webkit-transform: translate(0, -100%);
        transform: translate(0, -100%);
    }
}
@-webkit-keyframes btt-drawer-in {
    0% {
        -webkit-transform: translate(0, 100%);
        transform: translate(0, 100%);
    }
    100% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
}
@keyframes btt-drawer-in {
    0% {
        -webkit-transform: translate(0, 100%);
        transform: translate(0, 100%);
    }
    100% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
}
@-webkit-keyframes btt-drawer-out {
    0% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
    100% {
        -webkit-transform: translate(0, 100%);
        transform: translate(0, 100%);
    }
}
@keyframes btt-drawer-out {
    0% {
        -webkit-transform: translate(0, 0);
        transform: translate(0, 0);
    }
    100% {
        -webkit-transform: translate(0, 100%);
        transform: translate(0, 100%);
    }
}
.iu-drawer {
    position: absolute;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    background-color: #fff;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    -webkit-box-shadow: 0 8px 10px -5px rgba(0, 0, 0, 0.2), 0 16px 24px 2px rgba(0, 0, 0, 0.14),
        0 6px 30px 5px rgba(0, 0, 0, 0.12);
    box-shadow: 0 8px 10px -5px rgba(0, 0, 0, 0.2), 0 16px 24px 2px rgba(0, 0, 0, 0.14),
        0 6px 30px 5px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}
.iu-drawer.rtl {
    -webkit-animation: rtl-drawer-out 0.3s;
    animation: rtl-drawer-out 0.3s;
    right: 0;
}
.iu-drawer__open .iu-drawer.rtl {
    -webkit-animation: rtl-drawer-in 0.3s 1ms;
    animation: rtl-drawer-in 0.3s 1ms;
}
.iu-drawer.ltr {
    -webkit-animation: ltr-drawer-out 0.3s;
    animation: ltr-drawer-out 0.3s;
    left: 0;
}
.iu-drawer__open .iu-drawer.ltr {
    -webkit-animation: ltr-drawer-in 0.3s 1ms;
    animation: ltr-drawer-in 0.3s 1ms;
}
.iu-drawer.ttb {
    -webkit-animation: ttb-drawer-out 0.3s;
    animation: ttb-drawer-out 0.3s;
    top: 0;
}
.iu-drawer__open .iu-drawer.ttb {
    -webkit-animation: ttb-drawer-in 0.3s 1ms;
    animation: ttb-drawer-in 0.3s 1ms;
}
.iu-drawer.btt {
    -webkit-animation: btt-drawer-out 0.3s;
    animation: btt-drawer-out 0.3s;
    bottom: 0;
}
.iu-drawer__open .iu-drawer.btt {
    -webkit-animation: btt-drawer-in 0.3s 1ms;
    animation: btt-drawer-in 0.3s 1ms;
}
.iu-drawer__wrapper {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
    margin: 0;
}
.iu-drawer__header {
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    color: #72767b;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    margin-bottom: 32px;
    padding: 20px 20px 0;
}
.iu-drawer__header > :first-child {
    -webkit-box-flex: 1;
    -ms-flex: 1;
    flex: 1;
}
.iu-drawer__title {
    margin: 0;
    -webkit-box-flex: 1;
    -ms-flex: 1;
    flex: 1;
    line-height: inherit;
    font-size: 1rem;
}
.iu-drawer__close-btn {
    border: none;
    cursor: pointer;
    font-size: 20px;
    color: inherit;
    background-color: transparent;
}
.iu-drawer__body {
    -webkit-box-flex: 1;
    -ms-flex: 1;
    flex: 1;
}
.iu-drawer__body > * {
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
}
.iu-drawer__container {
    position: relative;
}
.iu-drawer-fade-enter-active {
    -webkit-animation: iu-drawer-fade-in 0.3s;
    animation: iu-drawer-fade-in 0.3s;
}
.iu-drawer-fade-leave-active {
    animation: iu-drawer-fade-in 0.3s reverse;
}
</style>
